import { FC, memo, useCallback, useEffect, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import clsx from 'clsx';

import { Button } from '../button/Button';
import { Tooltip } from '../tooltip/Tooltip';
import { useStyles } from './confirmationTooltip.styles';
import { ConfirmationTooltipProps } from './confirmationTooltip.types';

export const ConfirmationTooltip: FC<ConfirmationTooltipProps> = memo(
  ({
    disabled,
    confirmationDisabled,
    toggle,
    confirmButtonText,
    cancelButtonText,
    isLoading,
    title,
    children,
    fullWidthButton,
    placement,
    zIndex,
    onConfirm,
    onClose,
    onVisibleChange,
  }) => {
    const styles = useStyles({ fullWidthButton });

    const [isVisible, setVisibility] = useState(false);

    const handleToggle = useCallback(() => {
      setVisibility(!isVisible);

      if (isVisible) {
        onClose?.();
      }
    }, [isVisible, onClose]);

    const handleClose = useCallback(() => {
      setVisibility(false);
      onClose?.();
    }, [onClose]);

    const handleConfirm = useCallback(async () => {
      try {
        await onConfirm();
        setVisibility(false);
      } catch (error) {
        console.error(error);
      }
    }, [onConfirm]);

    useEffect(() => {
      onVisibleChange?.(isVisible);
    }, [isVisible, onVisibleChange]);

    return (
      <Tooltip
        isClick
        isArrow
        interactive
        placement={placement}
        zIndex={zIndex}
        open={isVisible && !disabled}
        className={styles.root}
        title={
          <div>
            {title && (
              <Typography variant="h5" className={styles.label}>
                {title}
              </Typography>
            )}

            <div className={styles.tooltipTitle}>{children}</div>

            <Grid container justifyContent="flex-end" spacing={1}>
              <Grid item>
                <Button type="button" size="medium" color="primary" onClick={handleClose}>
                  {cancelButtonText || 'Cancel'}
                </Button>
              </Grid>

              <Grid item>
                <Button
                  buttonType="twoTone"
                  size="medium"
                  isLoading={isLoading}
                  disabled={confirmationDisabled}
                  style={{ width: 136 }}
                  onClick={handleConfirm}
                >
                  {confirmButtonText || 'Confirm'}
                </Button>
              </Grid>
            </Grid>
          </div>
        }
        onClose={handleClose}
      >
        <div className={clsx('confirmation-toggle-container', styles.toggleContainer)} onClick={handleToggle}>
          {toggle}
        </div>
      </Tooltip>
    );
  },
);
